import { LengthUnits } from "@/libs/units/length";
import { isNullOrUndefined } from "@/utils/utils";

import type { BaseEquipment } from "@/composables/equipment/equipmentTypes";
import type { XrefPumps } from "@/composables/forms/formTypes";

export type EquipmentMeasurements = {
  weight_pounds: number;
  length_inches: number;
  neck_outer_diameter_inches: number;
  neck_inner_diameter_inches: number;
  outer_diameter_inches: number;
  inner_diameter_inches: number;
};

export type BaseLengthEquipment = BaseEquipment &
  EquipmentMeasurements & {
    display_label?: string;
    ref?: unknown;
  };

export type BaseLengthItem = EquipmentMeasurements & {
  startingStringLengthFt: number;
  endingStringLengthFt: number;
  equipmentLengthFt: number;
  startingDepthFt: number;
  endingDepthFt: number;
  measuredDepthFt: number;
};

export type PumpEquipmentLengthItem = BaseLengthItem & {
  type: "pump";
  pump: XrefPumps[number];
};

export type EquipmentLengthItem = BaseLengthItem & {
  type: "intake" | "seal" | "motor" | "sensor";
  xref: BaseLengthEquipment;
};

export type LengthItem = EquipmentLengthItem | PumpEquipmentLengthItem;

/**
 * Equipment string geometry, extracted from
 * `useDeviationBendingStressChartData` so the bending stress calcs (SPYG-88) can
 * consume it without going through the chart composable.
 *
 * Column references below are to `Final_Landing` in
 * Bending.Stress.Version.1.2.xlsx.
 */

export type UnpositionedPumpItem = EquipmentMeasurements & {
  type: "pump";
  pump: XrefPumps[number];
  equipmentLengthFt: number;
};

export type EquipmentStringSource = {
  tubingLengthFt: number;
  pumps: UnpositionedPumpItem[];
  intakes: BaseLengthEquipment[];
  seals: BaseLengthEquipment[];
  motors: BaseLengthEquipment[];
  sensors: BaseLengthEquipment[];
};

export type EquipmentSegmentProperties = {
  /** col K - outer radius in play at this depth (housing or neck) */
  outerRadiusInches: number;
  /** col L - inner radius in play at this depth (housing or neck) */
  innerRadiusInches: number;
  /** col M - pi/4 * (r_o^4 - r_i^4) */
  momentOfInertia: number;
  /** col J - segment weight divided by segment length */
  weightLbsPerInch: number;
  /** col H - length of the joint/segment this depth falls inside */
  jointLengthFt: number;
  /** true when the depth falls on the narrower neck rather than the housing */
  isNeck: boolean;
};

export function extractMeasurements(
  ref: EquipmentMeasurements
): EquipmentMeasurements {
  return {
    neck_outer_diameter_inches: ref.neck_outer_diameter_inches ?? 0,
    neck_inner_diameter_inches: ref.neck_inner_diameter_inches ?? 0,
    outer_diameter_inches: ref.outer_diameter_inches ?? 0,
    inner_diameter_inches: ref.inner_diameter_inches ?? 0,
    length_inches: ref.length_inches ?? 0,
    weight_pounds: ref.weight_pounds ?? 0
  };
}

/**
 * Second moment of area for a hollow circular section: `pi/4 * (r_o^4 - r_i^4)`.
 * Matches cols I/J on the equipment header block and col M in the main table.
 *
 * The sheet's own labels at `I7` and `J7` are swapped: `I7` reads "Moment of
 * Inertia Neck" but `I8 = PI()/4*(K8^4-L8^4)` uses the **housing** radii, and
 * `J7` reads "Housing" while using the **neck** radii. The formulas are right
 * and the magnitudes confirm it (`I8` 5.53 housing vs `J8` 2.08 neck), so we
 * follow the formulas.
 */
export function getMomentOfInertia(
  outerRadiusInches: number,
  innerRadiusInches: number
): number {
  return (
    (Math.PI / 4) *
    (Math.pow(outerRadiusInches, 4) - Math.pow(innerRadiusInches, 4))
  );
}

/**
 * Expands the pump xrefs into the individual segments the string is actually
 * made of. A pump with a `housings_order` contributes one segment per housing
 * (each with its own length and weight); a pump without housings contributes a
 * single segment. Pump segments always report the *housing* bore, not the pump
 * bore.
 *
 * Housing length always comes from the xref record, never the catalog: the
 * xref is the scenario's own value and may legitimately differ. Weight comes
 * from the catalog because the xref has none.
 *
 * Lifted verbatim in behaviour from
 * `useDeviationBendingStressChartData.equipmentDepthsFt`.
 */
export function buildPumpEquipmentItems(
  pumps: XrefPumps
): UnpositionedPumpItem[] {
  return pumps
    .map(pump => {
      if (!pump?.length_feet) {
        throw new Error("Pump length_inches is required");
      }

      const pumpData = { ...pump, ...pump.r_pump };

      if (pump.housings_order?.length && pump.housings_order.length >= 1) {
        return pump.housings_order.map(housingId => {
          const housing = pump.r_xref_housings.find(h => h.id === housingId);
          if (!housing?.length_inches) {
            throw new Error(
              `Expected length inches to be present on the housing.`
            );
          }

          return {
            type: "pump" as const,
            pump,
            ...extractMeasurements({
              ...pumpData,
              length_inches: housing.length_inches,
              weight_pounds: housing.r_housing?.weight_pounds,
              inner_diameter_inches: pumpData.housing_inner_diameter_inches,
              outer_diameter_inches: pumpData.housing_outer_diameter_inches
            } as EquipmentMeasurements),
            equipmentLengthFt: housing.length_inches / 12
          };
        });
      }

      return {
        type: "pump" as const,
        pump,
        ...extractMeasurements({
          ...pumpData,
          inner_diameter_inches: pumpData.housing_inner_diameter_inches,
          outer_diameter_inches: pumpData.housing_outer_diameter_inches,
          length_inches: pump.length_feet * 12
        } as EquipmentMeasurements),
        equipmentLengthFt: pump.length_feet
      };
    })
    .flat();
}

/**
 * Lays the equipment string out below the tubing, accumulating measured depth in
 * run order: pumps (expanded per housing) -> intakes -> seals -> motors ->
 * sensors. Mirrors rows 8..26 of the sheet.
 *
 * Behaviour is intentionally identical to the block this replaces in
 * `useDeviationBendingStressChartData.equipmentDepthsFt`.
 */
export function buildEquipmentStringSegments(
  source: EquipmentStringSource
): LengthItem[] {
  const { tubingLengthFt, pumps, intakes, seals, motors, sensors } = source;

  let accumulatedDepth = tubingLengthFt;

  const positionedPumps: PumpEquipmentLengthItem[] = pumps.map(pump => {
    const startingDepthFt = accumulatedDepth;
    const startingStringLengthFt = accumulatedDepth - tubingLengthFt;
    const equipmentLengthFt = pump.equipmentLengthFt;
    accumulatedDepth += equipmentLengthFt;
    const endingDepthFt = accumulatedDepth;

    return {
      ...pump,
      startingDepthFt,
      endingDepthFt,
      measuredDepthFt: endingDepthFt,
      startingStringLengthFt,
      endingStringLengthFt: accumulatedDepth - tubingLengthFt
    };
  });

  const groups = [
    { type: "intake" as const, xrefs: intakes },
    { type: "seal" as const, xrefs: seals },
    { type: "motor" as const, xrefs: motors },
    { type: "sensor" as const, xrefs: sensors }
  ];

  const baseItems: EquipmentLengthItem[] = [];

  groups.forEach(group => {
    group.xrefs.forEach(xref => {
      const startingDepthFt = accumulatedDepth;
      const startingStringLengthFt = accumulatedDepth - tubingLengthFt;
      const equipmentLengthFt = LengthUnits.Inches.toStandardUnit(
        xref.length_inches ?? 0
      );
      accumulatedDepth += equipmentLengthFt;
      const endingDepthFt = accumulatedDepth;
      const endingStringLengthFt = accumulatedDepth - tubingLengthFt;

      baseItems.push({
        type: group.type,
        xref,
        equipmentLengthFt,
        startingDepthFt,
        endingDepthFt,
        measuredDepthFt: endingDepthFt,
        startingStringLengthFt,
        endingStringLengthFt,
        ...xref
      });
    });
  });

  return [...positionedPumps, ...baseItems].filter(item =>
    [
      item.outer_diameter_inches,
      item.inner_diameter_inches,
      item.neck_inner_diameter_inches,
      item.neck_outer_diameter_inches
    ].every(value => !isNullOrUndefined(value))
  );
}

/**
 * Diameters in play at a depth. Above the segment's own start the string is on
 * the narrower neck; inside the segment it is on the housing. Below the segment
 * nothing applies.
 *
 * This is the v1.1 arrangement. v1.2 moved the neck to the **bottom** 0.5 ft of
 * each item and the tubing coupling to the **top** 0.5 ft of the joint; see
 * `bendingStress.ts::buildStressStringItems`, which is what the bending stress
 * model now uses. This copy still serves the schematic and equipment lookups.
 */
export function getEquipmentDiametersAtDepth(
  measuredDepthFt: number,
  equipment: Pick<
    LengthItem,
    | "inner_diameter_inches"
    | "outer_diameter_inches"
    | "neck_inner_diameter_inches"
    | "neck_outer_diameter_inches"
    | "startingDepthFt"
    | "endingDepthFt"
  >
): { outer: number; inner: number; isNeck: boolean } {
  if (measuredDepthFt < equipment.startingDepthFt) {
    return {
      outer: equipment.neck_outer_diameter_inches,
      inner: equipment.neck_inner_diameter_inches,
      isNeck: true
    };
  }
  if (measuredDepthFt <= equipment.endingDepthFt) {
    return {
      outer: equipment.outer_diameter_inches,
      inner: equipment.inner_diameter_inches,
      isNeck: false
    };
  }
  return { outer: 0, inner: 0, isNeck: false };
}

/**
 * The segment covering a measured depth, or the deepest segment once we run past
 * the bottom of the string.
 */
export function findEquipmentSegmentAtMD(
  measuredDepthFt: number,
  segments: LengthItem[]
): LengthItem | null {
  if (!segments.length) return null;

  const index = segments.findIndex(
    (segment, i) =>
      segment.measuredDepthFt >= measuredDepthFt || i === segments.length - 1
  );

  return index < 0 ? null : segments[index];
}

/**
 * Everything the per-step bending stress table needs at one measured depth:
 * cols H, J, K, L and M.
 */
export function getEquipmentSegmentAtMD(
  measuredDepthFt: number,
  segments: LengthItem[]
): EquipmentSegmentProperties | null {
  const segment = findEquipmentSegmentAtMD(measuredDepthFt, segments);
  if (!segment) return null;

  const { outer, inner, isNeck } = getEquipmentDiametersAtDepth(
    measuredDepthFt,
    segment
  );

  const outerRadiusInches = outer / 2;
  const innerRadiusInches = inner / 2;
  const lengthInches = segment.length_inches ?? 0;

  return {
    outerRadiusInches,
    innerRadiusInches,
    momentOfInertia: getMomentOfInertia(outerRadiusInches, innerRadiusInches),
    weightLbsPerInch:
      lengthInches > 0 ? (segment.weight_pounds ?? 0) / lengthInches : 0,
    jointLengthFt: segment.equipmentLengthFt,
    isNeck
  };
}
